import { useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../components/Config.js';

export default function useKeywords({
  responseJson,
  setResponseJson,
  rankedResumesWithOriginalIndex,
  setRankedResumesWithOriginalIndex,
  setIsLoading,
  onConfetti,
}) {
  const [editableKeywords, setEditableKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [newKeywords, setNewKeywords] = useState({});
  const [keywordVersion, setKeywordVersion] = useState(0);
  const [keywordFeedback, setKeywordFeedback] = useState({ message: '', isError: false });
  const [keywordsLocked, setKeywordsLocked] = useState(false);

  const [keywordMethods] = useState(['rake', 'yake']);
  const [useSpacyNext, setUseSpacyNext] = useState(false);
  const [currentMethodIndex, setCurrentMethodIndex] = useState(0);
  const [isLoadingMoreKeywords, setIsLoadingMoreKeywords] = useState(false);
  const [shouldHideButton, setShouldHideButton] = useState(false);
  const [copyStatus, setCopyStatus] = useState({});

  useEffect(() => {
    if (responseJson?.job_description_keywords) {
      setEditableKeywords([...responseJson.job_description_keywords]);
      setKeywordVersion(0);
    }
  }, [responseJson]);

  const handleRemoveKeyword = (keywordToRemove) => {
    if (keywordsLocked) {
      setKeywordFeedback({ message: 'Cannot remove keywords - unlock first', isError: true });
      return;
    }
    if (!responseJson?.job_description_keywords) return;
    const updatedKeywords = editableKeywords.filter((kw) => kw !== keywordToRemove);
    setEditableKeywords(updatedKeywords);
    reanalyzeWithUpdatedKeywords(updatedKeywords);
  };

  const clearAllKeywords = () => {
    if (keywordsLocked) {
      setKeywordFeedback({ message: 'Cannot clear keywords while locked.', isError: true });
      return;
    }
    if (window.confirm('Are you sure you want to clear all keywords? This action cannot be undone.')) {
      const numCleared = editableKeywords.length;
      setEditableKeywords([]);
      setUseSpacyNext(true);
      setCurrentMethodIndex(0);
      setShouldHideButton(false);
      reanalyzeWithUpdatedKeywords([]);
      setKeywordFeedback({
        message: (
          <p className="keyword-feedback success">Cleared {numCleared} keyword(s)!</p>
        ),
        isError: false,
      });
    }
  };

  const copyKeywordsToClipboard = (keywords) => {
    const sortedKeywords = [...keywords].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    const keywordText = sortedKeywords.join(', ');
    navigator.clipboard
      .writeText(keywordText)
      .then(() => {
        setCopyStatus({ copied: true, time: Date.now() });
        setTimeout(() => setCopyStatus({}), 2000);
      })
      .catch(() => {
        setCopyStatus({ error: true });
        setTimeout(() => setCopyStatus({}), 2000);
      });
  };

  const handleAddKeyword = () => {
    if (keywordsLocked) {
      setKeywordFeedback({ message: 'Cannot add keywords - unlock first', isError: true });
      return;
    }
    const trimmedInput = newKeyword.trim();
    if (!trimmedInput) {
      setKeywordFeedback({ message: 'Keyword cannot be empty', isError: true });
      return;
    }
    const potentialKeywords = trimmedInput
      .split(',')
      .map((kw) => kw.trim())
      .filter((kw) => kw.length > 0);

    if (potentialKeywords.length === 0) {
      setKeywordFeedback({ message: 'No valid keywords found', isError: true });
      return;
    }

    const duplicates = [];
    const newKeywordsToAdd = [];
    const existingLower = editableKeywords.map((kw) => kw.toLowerCase());
    potentialKeywords.forEach((kw) => {
      const lower = kw.toLowerCase();
      const inputDuplicate = potentialKeywords.filter((k) => k.toLowerCase() === lower).length > 1;
      const existingDuplicate = existingLower.includes(lower);
      if (inputDuplicate || existingDuplicate) {
        duplicates.push(kw);
      } else {
        newKeywordsToAdd.push(kw);
      }
    });

    if (duplicates.length > 0 && newKeywordsToAdd.length === 0) {
      setKeywordFeedback({
        message:
          duplicates.length === 1
            ? `"${duplicates[0]}" already exists or is duplicated`
            : `These keywords already exist or are duplicated: ${duplicates.join(', ')}`,
        isError: true,
      });
      return;
    }

    if (newKeywordsToAdd.length > 0) {
      const updatedKeywords = [...editableKeywords, ...newKeywordsToAdd];
      const newKeywordsWithTimestamps = newKeywordsToAdd.reduce((acc, kw) => {
        acc[kw] = Date.now();
        return acc;
      }, {});
      setNewKeywords((prev) => ({ ...prev, ...newKeywordsWithTimestamps }));
      setNewKeyword('');
      reanalyzeWithUpdatedKeywords(updatedKeywords);

      let feedbackMessage;
      if (duplicates.length > 0) {
        feedbackMessage = (
          <p className="keyword-feedback success">Added {newKeywordsToAdd.length} new keywords ({duplicates.length} duplicates skipped)</p>
        );
      } else {
        feedbackMessage =
          newKeywordsToAdd.length === 1 ? (
            <p className="keyword-feedback success">"{newKeywordsToAdd[0]}" successfully added!</p>
          ) : (
            <p className="keyword-feedback success">Added {newKeywordsToAdd.length} new keywords!</p>
          );
      }
      setKeywordFeedback({ message: feedbackMessage, isError: false });
      setTimeout(() => setKeywordFeedback({ message: '', isError: false }), 8000);
    }
  };

  const loadMoreKeywords = async () => {
    if (keywordsLocked) {
      setKeywordFeedback({ message: 'Cannot load more keywords - unlock first', isError: true });
      return;
    }

    let currentMethod;
    if (useSpacyNext) {
      currentMethod = 'spacy';
      setUseSpacyNext(false);
      setCurrentMethodIndex(0);
    } else {
      if (currentMethodIndex >= keywordMethods.length || !responseJson?.job_description) {
        setIsLoadingMoreKeywords(false);
        return;
      }
      currentMethod = keywordMethods[currentMethodIndex];
    }

    setIsLoadingMoreKeywords(true);
    try {
      setNewKeywords({});
      const res = await fetch(`${API_BASE}/get_keywords_by_method`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_description: responseJson.job_description,
          current_keywords: editableKeywords,
          method: currentMethod,
        }),
      });
      const data = await res.json();
      const isLastMethod = !useSpacyNext && currentMethodIndex === keywordMethods.length - 1;

      if (data.status === 'success') {
        setNewKeywords({});
        if (data.keywords.length === 0) {
          setKeywordFeedback({
            message: isLastMethod
              ? 'No new keywords found. All extraction methods completed.'
              : 'No new keywords found using current method.',
            isError: false,
          });
        } else {
          const newKeywordsWithTimestamps = data.keywords.reduce((acc, kw) => {
            acc[kw] = Date.now();
            return acc;
          }, {});
          setNewKeywords(newKeywordsWithTimestamps);
          const updatedKeywords = [...editableKeywords, ...data.keywords];
          await reanalyzeWithUpdatedKeywords(updatedKeywords);
          setKeywordFeedback({
            message: isLastMethod ? (
              <p className="keyword-feedback success">Added {data.keywords.length} new keywords! All extraction methods completed.</p>
            ) : (
              <p className="keyword-feedback success">Added {data.keywords.length} new keywords!</p>
            ),
            isError: false,
          });
        }
        onConfetti?.();
        setTimeout(() => setKeywordFeedback({ message: '', isError: false }), 8000);
      } else {
        setKeywordFeedback({
          message: isLastMethod ? 'No new keywords added. All extraction methods completed.' : 'No new keywords added.',
          isError: false,
        });
      }
    } catch (_err) {
      setKeywordFeedback({ message: 'Error finding keywords.', isError: true });
    } finally {
      if (!useSpacyNext) {
        setCurrentMethodIndex((prev) => {
          const nextIndex = prev + 1;
          if (nextIndex >= keywordMethods.length) {
            setShouldHideButton(true);
            setTimeout(() => setKeywordFeedback({ message: '', isError: false }), 8000);
            return nextIndex;
          }
          setTimeout(() => setKeywordFeedback({ message: '', isError: false }), 8000);
          return nextIndex;
        });
      }
      setIsLoadingMoreKeywords(false);
    }
  };

  const reanalyzeWithUpdatedKeywords = async (keywords) => {
    setIsLoading(true);
    try {
      const previousKeywords = responseJson.job_description_keywords || [];
      const resumesToSend = rankedResumesWithOriginalIndex.map((r) => ({
        filename: r.filename,
        label: r.label,
        content: r.content,
        matching_keywords: r.matching_keywords,
        originalIndex: r.originalIndex,
      }));

      const res = await fetch(`${API_BASE}/reanalyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_title: responseJson.job_title,
          job_description: responseJson.job_description,
          resumes: resumesToSend,
          keywords: keywords,
          previous_keywords: previousKeywords,
          keyword_version: keywordVersion + 1,
        }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        const updatedResumes = data.ranked_resumes.map((resume) => {
          const originalResume = rankedResumesWithOriginalIndex.find((r) => r.filename === resume.filename);
          return { ...resume, originalIndex: originalResume?.originalIndex ?? -1 };
        });
        setResponseJson((prev) => ({
          ...prev,
          ranked_resumes: data.ranked_resumes,
          job_description_keywords: data.job_description_keywords,
          previous_keywords: data.job_description_keywords,
        }));
        setEditableKeywords(data.job_description_keywords);
        setKeywordVersion((prev) => prev + 1);
        setRankedResumesWithOriginalIndex(updatedResumes);
      }
    } catch (_err) {
      // swallow
    } finally {
      setIsLoading(false);
    }
  };

  const toggleKeywordsLock = () => {
    if (keywordsLocked || editableKeywords.length > 0) {
      setKeywordsLocked((prev) => !prev);
      setKeywordFeedback({
        message: keywordsLocked
          ? 'Keywords unlocked - will extract from job description'
          : 'Keywords locked - compare against current set',
        isError: false,
      });
      setTimeout(() => setKeywordFeedback({ message: '', isError: false }), 7000);
    } else {
      setKeywordFeedback({ message: 'No keywords to lock - extract some first', isError: true });
    }
  };

  return {
    editableKeywords,
    setEditableKeywords,
    newKeyword,
    setNewKeyword,
    newKeywords,
    keywordFeedback,
    setKeywordFeedback,
    keywordsLocked,
    setKeywordsLocked,
    keywordMethods,
    useSpacyNext,
    setUseSpacyNext,
    currentMethodIndex,
    isLoadingMoreKeywords,
    shouldHideButton,
    copyStatus,
    handleRemoveKeyword,
    clearAllKeywords,
    copyKeywordsToClipboard,
    handleAddKeyword,
    loadMoreKeywords,
    reanalyzeWithUpdatedKeywords,
    toggleKeywordsLock,
  };
}


